import React, { useState, useRef, useEffect } from 'react';
import { MapPin, ChevronDown, Search, X } from 'lucide-react';

/**
 * SearchableCitySelect
 *
 * Props:
 *   cities        — array of city objects: [{ id, name }, ...]
 *   value         — currently selected city name (string)
 *   onValueChange — callback(cityName: string)
 *   placeholder   — string, default "Select City"
 *   showIcon      — bool, show MapPin icon before trigger (default false)
 *   triggerClassName — extra Tailwind classes on the trigger button
 *   fullWidth     — bool, makes both trigger AND dropdown panel fill parent (default false)
 */
const SearchableCitySelect = ({
  cities = [],
  value,
  onValueChange,
  placeholder = 'Select City',
  showIcon = false,
  triggerClassName = '',
  fullWidth = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = query.trim()
    ? cities.filter(c => c.name.toLowerCase().includes(query.toLowerCase()))
    : cities;

  const selectedLabel = value
    ? (cities.find(c => c.name === value)?.name?.toUpperCase() ?? placeholder)
    : placeholder;

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleSelect = (cityName) => {
    onValueChange(cityName);
    setOpen(false);
    setQuery('');
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        display: fullWidth ? 'block' : 'inline-block',
        width: fullWidth ? '100%' : undefined,
      }}
    >
      {/* ── Trigger button ── */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium bg-white hover:border-orange-400 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300 ${triggerClassName}`}
        style={{
          width: fullWidth ? '100%' : undefined,
          minWidth: fullWidth ? undefined : '130px',
          justifyContent: 'space-between',
        }}
      >
        <span className="flex items-center gap-1.5 truncate flex-1 min-w-0">
          {showIcon && <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" />}
          <span className="truncate text-gray-700">{selectedLabel}</span>
        </span>
        <ChevronDown
          className="h-4 w-4 text-gray-400 flex-shrink-0 ml-1 transition-transform"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {/* ── Dropdown panel ── */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            /* full-width mode stretches to match trigger; otherwise fixed 220px */
            width: fullWidth ? '100%' : '220px',
            zIndex: 9999,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          }}
        >
          {/* Search bar */}
          <div style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5">
              <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search city..."
                className="flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
                style={{ minWidth: 0 }}
              />
              {query && (
                <button type="button" onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* City list */}
          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">No cities found</div>
            ) : (
              filtered.map(city => (
                <button
                  key={city.id}
                  type="button"
                  onClick={() => handleSelect(city.name)}
                  className="w-full text-left px-4 py-2.5 text-sm font-medium tracking-wide hover:bg-orange-50 hover:text-orange-700 transition-colors"
                  style={{
                    background: value === city.name ? '#fff7ed' : 'transparent',
                    color: value === city.name ? '#c2410c' : '#374151',
                    borderLeft: value === city.name ? '3px solid #f97316' : '3px solid transparent',
                  }}
                >
                  {city.name.toUpperCase()}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableCitySelect;